// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Legacy code
import { action, autorun, observable, makeObservable } from 'mobx';
import { createContext, useContext } from 'react';

import { getOrCreateApiGateway } from '../server/apiGateway';
import { getOrCreateLogger } from '../utils/logger';
import { type AuthStore } from './authStore';
import { NavinaCache } from './cache';

const VALUES_DICTIONARY_LOCAL_STORAGE_KEY = 'UserConfig';

interface ValuesDictionaryType {
	readonly [key: string]: any;
}

export class UserConfigStore {
	valuesDictionary: ValuesDictionaryType = {};

	constructor(authStore: AuthStore | undefined) {
		makeObservable(this, {
			valuesDictionary: observable,
			setValuesDictionary: action,
			setValue: action,
			parseUserConfig: action,
		});

		if (!authStore) {
			getOrCreateLogger().error('no authStore, scheduleStore initialization failed');
			return;
		}

		// eslint-disable-next-line no-restricted-syntax -- Legacy code
		autorun((): void => {
			if (!authStore.getToken || window.location.pathname.includes('epic/login')) {
				console.log('no token, user not yet authenticated');
				return;
			}

			const userConfigStr = NavinaCache.get(VALUES_DICTIONARY_LOCAL_STORAGE_KEY);
			if (userConfigStr) {
				this.parseUserConfig(userConfigStr);
			}

			getOrCreateApiGateway()
				.getUserConfig()
				.then((userConfigResponse): void => {
					if (userConfigResponse?.data?.result) {
						this.setValuesDictionary(userConfigResponse?.data?.result);
						NavinaCache.set(VALUES_DICTIONARY_LOCAL_STORAGE_KEY, JSON.stringify(this.valuesDictionary));
					} else {
						console.warn('Unable to retrieve user config', userConfigResponse);
					}
				})
				.catch((err): void => console.warn('Error retrieving user config', err));
		});
	}

	readonly setValuesDictionary = (valuesDictionary: typeof this.valuesDictionary): void => {
		this.valuesDictionary = valuesDictionary;
	};

	// Rename to a descriptive name
	readonly setValuesDictionaryAndCallback = (
		valuesDictionary: ValuesDictionaryType,
		callback?: (isSuccess: boolean, errorMessage?: string) => void,
	): void => {
		const oldValues = { ...this.valuesDictionary };

		this.setValuesDictionary(valuesDictionary);

		NavinaCache.set(VALUES_DICTIONARY_LOCAL_STORAGE_KEY, JSON.stringify(valuesDictionary));

		getOrCreateApiGateway()
			.updateUserConfig(valuesDictionary)
			.then((response): void => {
				callback?.(Boolean(response?.data?.result));
			})
			.catch((error): void => {
				console.warn('Unable to set user config', error);
				callback?.(false, error.message || error);
				this.setValuesDictionary(oldValues);
				NavinaCache.set(VALUES_DICTIONARY_LOCAL_STORAGE_KEY, JSON.stringify(oldValues));
			});
	};

	readonly setValue = (
		key: string,
		value: any,
		callback?: (isSuccess: boolean, errorMessage?: string) => void,
	): void => {
		const newValuesDictionary = { ...this.valuesDictionary };

		newValuesDictionary[key] = value;

		this.setValuesDictionaryAndCallback(newValuesDictionary, callback);
	};

	readonly parseUserConfig = (userConfigString: string): void => {
		try {
			if (userConfigString) {
				this.valuesDictionary = JSON.parse(userConfigString);
			}
		} catch (error) {
			console.error('Error parsing user config', { userConfigString, error });
		}
	};
}

// @ts-expect-error - Legacy code, nullability not yet enforced
export const UserConfigStoreContext = createContext<UserConfigStore>(null);
export const useUserConfigStore = (): UserConfigStore => useContext(UserConfigStoreContext);
